import React, { useEffect } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { observer } from 'mobx-react'
import { Alert } from '@instacart/tds'
import TDSDialog from '~shared/lib/TDSDialog/TDSDialog'
import { FetchStatus } from '~shared/lib/Types/FetchTypes'
import kdsStore from '../../Stores/KdsStore'
import { deviceTypeLabel } from '../../Types/SiteHealth'
import styles from './OfflineDevicesModal.module.scss'

dayjs.extend(relativeTime)

const formatLastSeen = (lastSeenUtc: string | null): string => {
  if (!lastSeenUtc) {
    return 'Never'
  }
  return dayjs(lastSeenUtc).fromNow()
}

export const OfflineDevicesModal: React.FC<{
  setOfflineDevicesModalOpen: (isVisible: boolean) => void
}> = observer(({ setOfflineDevicesModalOpen }) => {
  const { siteHealthFetch, fetchSiteHealth } = kdsStore
  const offlineDevices = siteHealthFetch.data ?? []

  useEffect(() => {
    fetchSiteHealth()
  }, [fetchSiteHealth])

  const isError = siteHealthFetch.status === FetchStatus.error

  const renderBody = () => {
    if (isError) {
      return (
        <Alert variant="error">
          <span>There was an error loading site health. Please try again later.</span>
        </Alert>
      )
    }

    if (offlineDevices.length === 0) {
      return <div className={styles.empty}>All devices for this site are online.</div>
    }

    return (
      <ul className={styles.deviceList} data-testid="offline-devices-list">
        {offlineDevices.map((device, index) => (
          <li
            key={`${device.deviceName ?? 'device'}-${index}`}
            className={styles.deviceRow}
            data-testid="offline-device-row"
          >
            <div className={styles.deviceMain}>
              <div className={styles.deviceName}>{device.deviceName ?? 'Unnamed device'}</div>
              <div className={styles.deviceMeta}>
                {deviceTypeLabel(device.deviceType)}
                {device.departmentName ? ` · ${device.departmentName}` : ''}
              </div>
            </div>
            <div className={styles.lastSeen}>Last seen {formatLastSeen(device.lastSeenUtc)}</div>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <TDSDialog
      toggleDialog={() => setOfflineDevicesModalOpen(false)}
      header="Offline Devices"
      customClass={styles.offlineDevicesModal}
      cancelButtonLabel="Close"
    >
      <div className={styles.body}>{renderBody()}</div>
    </TDSDialog>
  )
})

export default OfflineDevicesModal
